import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageList from '../components/messages/MessageList';
import MessageSearch from '../components/messages/MessageSearch';
import MessageEditor from '../components/messages/MessageEditor';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Bounce } from 'react-toastify';
import { toast } from '../utils/customToast';
import { useAuth } from '../context/AuthContext';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { apiFetch } from '../utils/api';
import DeleteModal from '../components/modals/DeleteModal';
import TodoPanel from '../components/todos/TodoPanel';
import FullTextModal from '../components/modals/FullTextModal';
import SearchCommandModal from '../components/modals/SearchCommandModal';
import KeyboardShortcutsButton from '../components/shared/KeyboardShortcutsButton';
import { useMessages } from '../context/MessagesContext';
import { StatusAccessRestriction, showRestrictedActionToast } from '../components/shared/TenantStatus';
import { useTenantStatus } from '../hooks/useTenantStatus';
import { FiChevronDown, FiEdit3, FiCopy, FiTag } from 'react-icons/fi';
import { usePlanPermissions, getCurrentPlan } from '../utils/planPermissions';
import useCustomTabs from '../hooks/useCustomTabs';
import MessageSortModal from '../components/modals/MessageSortModal';
import useMessageSettings from '../hooks/useMessageSettings';
import usePageTitle from '../hooks/usePageTitle';
import { useTutorial } from '../context/TutorialContext';
import EmailVerificationBanner from '../components/notifications/EmailVerificationBanner';

const Messages = () => {
    usePageTitle('Messages');
    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [editingMessage, setEditingMessage] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [columns, setColumns] = useState(3);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [todoOpen, setTodoOpen] = useState(false);
    const [todoTargetId, setTodoTargetId] = useState(null);
    const [fullMessage, setFullMessage] = useState(null);
    const [copied, setCopied] = useState({});
    const [showSearchModal, setShowSearchModal] = useState(false);
    // États pour la sélection multiple
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedMessages, setSelectedMessages] = useState(new Set());
    const [messagesToDelete, setMessagesToDelete] = useState([]);
    // État pour le dropdown
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const editorModalRef = useRef(null);
    const { getAuthHeaders, user, activeTenantId } = useAuth();
    const { handleApiError } = useApiErrorHandler();
    const { updateCachedMessages } = useMessages();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Tenant status and permissions
    const currentTenant = user?.tenants?.find(t => t._id === activeTenantId);
    const tenantStatus = useTenantStatus(currentTenant);
    
    // Vérifications des permissions
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canBulkDelete, canBulkDuplicate, canUseCustomTabs, canUseDateCommands, canMentionTodos, canMentionMessages, canMentionUsers, showNotifications } = usePlanPermissions(currentPlan);

    // Custom tabs — source unique, partagée avec MessageList via prop pour éviter le double fetch
    const customTabsApi = useCustomTabs();
    const { customTabs, addMessagesToTab, removeMessagesFromTab } = customTabsApi;
    const [showSortModal, setShowSortModal] = useState(false);
    
    // Tutorial
    const { start: startTutorial, notifyAction: tutorialNotify } = useTutorial();

    // Source unique pour les types/rôles — passé en props à MessageSearch pour éviter un double fetch
    const { messageTypes, messageRoles } = useMessageSettings();
    
    // Créer un objet de couleurs à partir des messageTypes
    const typeColors = React.useMemo(() => {
        const colors = {};
        messageTypes.forEach(type => {
            if (type.name && type.color) {
                colors[type.name] = type.color;
            }
        });
        return colors;
    }, [messageTypes]);
    
    // Vérifier le rôle utilisateur dans le tenant
    // Comparaison avec toString() car tenantId peut être un ObjectId et activeTenantId est un string
    const userTenant = user?.tenants?.find(t => 
        t.tenantId === activeTenantId || 
        t.tenantId?.toString() === activeTenantId ||
        t._id === activeTenantId ||
        t._id?.toString() === activeTenantId
    );
    
    const userRole = userTenant?.userRole;
    const isAdminOrOwner = userRole && ['owner', 'admin'].includes(userRole);

    // Mettre à jour le cache quand les messages changent
    useEffect(() => {
        updateCachedMessages(messages);
    }, [messages, updateCachedMessages]);

    // Gérer l'ouverture du TodoPanel depuis les notifications
    useEffect(() => {
        const todoId = sessionStorage.getItem('openTodoId');
        if (todoId) {
            setTodoTargetId(todoId);
            setTodoOpen(true);
            sessionStorage.removeItem('openTodoId');
        }
    }, [location]);

    // Écouter les événements pour ouvrir un todo ou un message
    useEffect(() => {
        const handleOpenTodo = (e) => {
            const id = e.detail?.id;
            if (id) {
                setTodoTargetId(id);
                setTodoOpen(true);
            }
        };

        const handleOpenMsg = (e) => {
            const id = e.detail?.id;
            if (id) {
                const msg = messages.find(m => m._id === id);
                if (msg) {
                    setFullMessage(msg);
                }
            }
        };

        window.addEventListener('openTodo', handleOpenTodo);
        window.addEventListener('openMsg', handleOpenMsg);

        return () => {
            window.removeEventListener('openTodo', handleOpenTodo);
            window.removeEventListener('openMsg', handleOpenMsg);
        };
    }, [messages]);

    // Gérer les raccourcis clavier
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ne pas déclencher si on est dans un champ de saisie
            const tag = document.activeElement?.tagName?.toLowerCase();
            const isEditing = tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable;

            // Escape : fermer l'éditeur s'il est ouvert (après que les sous-dropdowns aient consommé leur propre Escape)
            if (e.key === 'Escape' && showEditor) {
                e.preventDefault();
                setEditingMessage(null);
                setShowEditor(false);
                return;
            }

            // Ctrl+K (Windows/Linux) ou ⌘+K (Mac/iOS) pour ouvrir la recherche
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setShowSearchModal(true);
                tutorialNotify('search-opened');
                return;
            }

            // Ctrl+N (Windows/Linux) ou ⌘+N (Mac/iOS) pour créer un nouveau message
            if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !isEditing) {
                e.preventDefault();
                if (tenantStatus.canCreateMessage) {
                    setEditingMessage(null);
                    setShowEditor(true);
                }
                return;
            }

            // Ctrl+/ (Windows/Linux) ou ⌘+/ (Mac/iOS) pour afficher les raccourcis clavier
            if ((e.ctrlKey || e.metaKey) && e.key === '/') {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('openKeyboardShortcuts'));
                return;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [tenantStatus.canCreateMessage, showEditor, tutorialNotify]);

    // Gérer la sélection d'un message depuis le SearchCommandModal
    const handleSelectSearchMessage = (message) => {
        setFullMessage(message);
    };

    // Gérer les liens de partage (?shareMsg) et autres paramètres
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const shareMsgId = urlParams.get('shareMsg');
        const openMsgId = urlParams.get('openMsg');
        const canceled = urlParams.get('canceled');

        // Message partagé
        if (shareMsgId && messages.length > 0) {
            const msg = messages.find(m => m._id === shareMsgId);
            if (msg) {
                setFullMessage(msg);
                // Nettoyer l'URL
                navigate('/messages', { replace: true });
            }
        }

        // Ancienne logique : ouvrir un message via ?openMsg
        if (openMsgId && messages.length > 0) {
            const msg = messages.find(m => m._id === openMsgId);
            if (msg) {
                setFullMessage(msg);
                navigate('/messages', { replace: true });
            }
        }

        if (canceled === '1') {
            toast.info('Paiement annulé. Vous pouvez reprendre votre upgrade à tout moment.', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            // Nettoyer l'URL
            navigate('/messages', { replace: true });
        }
    }, [location.search, messages, navigate]);

    const handleCopy = async (id, htmlContent) => {
        try {
            // Créer un élément temporaire pour gérer le HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sanitizeHtml(htmlContent);
            
            // Copier à la fois le texte brut et le HTML formaté
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            
            // Utiliser l'API Clipboard moderne pour copier avec formatage
            if (navigator.clipboard && window.ClipboardItem) {
                const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
                const textBlob = new Blob([textContent], { type: 'text/plain' });
                
                const clipboardItem = new ClipboardItem({
                    'text/html': htmlBlob,
                    'text/plain': textBlob
                });
                
                await navigator.clipboard.write([clipboardItem]);
            } else {
                // Fallback pour les navigateurs plus anciens
                await navigator.clipboard.writeText(textContent);
            }
            
            setCopied(prev => ({ ...prev, [id]: true }));
            setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
        } catch (err) {
            console.error('Erreur lors de la copie:', err);
        }
    };

    const fetchMessages = React.useCallback(async () => {
        if (!activeTenantId) {
            return;
        }

        setLoading(true);
        try {
            const data = await apiFetch(
                `${process.env.REACT_APP_API_URL}/messages`,
                { headers: getAuthHeaders() },
                handleApiError
            );
            const messagesArray = Array.isArray(data) ? data : [];
            setMessages(messagesArray);
            setFilteredMessages(messagesArray);
        } catch (err) {
            console.error('Error fetching messages:', err);
            setMessages([]);
            setFilteredMessages([]);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders, activeTenantId, handleApiError]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    // Démarrer le tutoriel à la première visite (après chargement des messages)
    useEffect(() => {
        if (!loading) {
            const features = {
                canUseDateCommands,
                canMentionContent: canMentionTodos || canMentionMessages,
                canMentionUsers,
                showNotifications,
            };
            const timer = setTimeout(() => startTutorial(features), 800);
            return () => clearTimeout(timer);
        }
    }, [loading, startTutorial, canUseDateCommands, canMentionTodos, canMentionMessages, canMentionUsers, showNotifications]);

    // Écouter les mises à jour du profil pour rafraîchir les données
    useEffect(() => {
        const handleProfileUpdate = () => {
            fetchMessages();
        };
        
        window.addEventListener('userProfileUpdated', handleProfileUpdate);
        
        return () => {
            window.removeEventListener('userProfileUpdated', handleProfileUpdate);
        };
    }, [fetchMessages]);

    // Gérer la touche Escape pour sortir du mode de sélection
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && selectionMode) {
                setSelectionMode(false);
                setSelectedMessages(new Set());
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [selectionMode]);

    // Gérer la fermeture du dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const handleSearch = (searchTerm, type, role, person, priority) => {
        let filtered = messages;
        
        if (type && type !== 'Tous') {
            filtered = filtered.filter(message => message.type === type);
        }
        
        if (role && role !== 'Tous') {
            filtered = filtered.filter(message => message.role === role);
        }
        
        if (priority && priority !== 'Tous') {
            filtered = filtered.filter(message => message.priority === priority);
        }
        
        if (person && person !== 'Tous') {
            filtered = filtered.filter(message => message.createdByName === person);
        }
        
        if (searchTerm) {
            filtered = filtered.filter(message => {
                // Strip HTML tags from content for cleaner search
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = message.content;
                const contentText = tempDiv.textContent || tempDiv.innerText || '';
                
                return contentText.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       message.title.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        
        setFilteredMessages(filtered);
    };

    const addMessageLock = useRef(false);
    const handleAddMessage = async (newMessage) => {
        if (addMessageLock.current) return;
        addMessageLock.current = true;
        try {
            const saved = await apiFetch(
                `${process.env.REACT_APP_API_URL}/messages`,
                {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({
                        ...newMessage,
                        createdBy: user.id,
                        createdByName: user.name
                    }),
                },
                handleApiError
            );

            setMessages(prev => [...prev, saved]);
            setFilteredMessages(prev => [...prev, saved]);
            setShowEditor(false);
            tutorialNotify('message-created');
            toast.success('Message créé', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } catch (err) {
            console.error('Error creating message:', err);
        } finally {
            addMessageLock.current = false;
        }
    };

    const handleEdit = (message) => {
        setEditingMessage(message);
        setShowEditor(true);
    };

    const handleUpdateMessage = async (updatedMessage) => {
        if (!editingMessage) return;
        
        try {
            const saved = await apiFetch(
                `${process.env.REACT_APP_API_URL}/messages/${editingMessage._id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({
                        ...updatedMessage,
                        updatedBy: user.id,
                        updatedByName: user.name
                    }),
                },
                handleApiError
            );

            const updatedList = messages.map(m => m._id === saved._id ? saved : m);
            setMessages(updatedList);
            setFilteredMessages(updatedList);
            setEditingMessage(null);
            setShowEditor(false);
            toast.success('Message mis à jour', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } catch (err) {
            console.error('Error updating message:', err);
        }
    };

    const handleDelete = async (id) => {
        const message = messages.find(m => m._id === id);
        setMessagesToDelete([message]);
        setShowDeleteModal(true);
    };

    const handleSelectionModeToggle = () => {
        setSelectionMode(!selectionMode);
        if (selectionMode) {
            setSelectedMessages(new Set());
        }
    };

    const handleMessageSelect = (messageId) => {
        const newSelected = new Set(selectedMessages);
        if (newSelected.has(messageId)) {
            newSelected.delete(messageId);
        } else {
            newSelected.add(messageId);
        }
        setSelectedMessages(newSelected);
    };

    const handleDeleteSelected = () => {
        if (selectedMessages.size === 0) return;
        
        const messagesToDelete = Array.from(selectedMessages).map(id => 
            messages.find(m => m._id === id)
        ).filter(Boolean);
        
        setMessagesToDelete(messagesToDelete);
        setShowDeleteModal(true);
    };

    const handleDuplicateSelected = async () => {
        if (selectedMessages.size === 0) return;

        const ids = Array.from(selectedMessages);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/messages/bulk-duplicate`,
                {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                if (data.code === 'LIMIT_REACHED') {
                    toast.error(data.message, { position: 'top-right', autoClose: 6000, theme: 'light', transition: Bounce });
                } else {
                    toast.error(data.message || 'Erreur lors de la duplication', { position: 'top-right', autoClose: 5000, theme: 'light', transition: Bounce });
                }
                return;
            }

            const duplicated = data;
            const updated = [...duplicated, ...messages];
            setMessages(updated);
            setFilteredMessages(updated);
            updateCachedMessages(updated);

            setSelectionMode(false);
            setSelectedMessages(new Set());

            const count = duplicated.length;
            toast.success(
                count === 1 ? 'Message dupliqué' : `${count} messages dupliqués`,
                { position: 'top-right', autoClose: 5000, theme: 'light', transition: Bounce }
            );
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleSort = (messagesToSort) => {
        if (!canUseCustomTabs) return;
        
        // Si c'est un Set, on l'utilise tel quel, sinon on crée un Set avec un seul message
        let messageSet;
        if (messagesToSort instanceof Set) {
            messageSet = messagesToSort;
        } else {
            messageSet = new Set([messagesToSort]);
        }
        
        // On met à jour temporairement selectedMessages pour la modal
        setSelectedMessages(messageSet);
        setShowSortModal(true);
    };

    const handleSelectAll = () => {
        if (selectedMessages.size === filteredMessages.length) {
            setSelectedMessages(new Set());
        } else {
            setSelectedMessages(new Set(filteredMessages.map(m => m._id)));
        }
    };

    const confirmDelete = async () => {
        if (messagesToDelete.length === 0) return;

        try {
            // Supprimer tous les messages sélectionnés
            await Promise.all(
                messagesToDelete.map(message => 
                    fetch(`${process.env.REACT_APP_API_URL}/messages/${message._id}`, { 
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    })
                )
            );

            const deletedIds = new Set(messagesToDelete.map(m => m._id));
            const updatedList = messages.filter(m => !deletedIds.has(m._id));
            setMessages(updatedList);
            setFilteredMessages(updatedList);
            
            // Nettoyer l'état d'édition si le message en cours d'édition a été supprimé
            if (editingMessage && deletedIds.has(editingMessage._id)) {
                setEditingMessage(null);
            }
            
            // Sortir du mode de sélection et nettoyer la sélection
            setSelectionMode(false);
            setSelectedMessages(new Set());
            
            const count = messagesToDelete.length;
            toast.success(
                count === 1 ? 'Message supprimé' : `${count} messages supprimés`,
                {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                }
            );
        } catch (err) {
            toast.error('Erreur lors de la suppression des messages', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setShowDeleteModal(false);
            setMessagesToDelete([]);
        }
    };

    const handleReorder = async (orderedIds) => {
        const idToMsg = Object.fromEntries(messages.map(m => [m._id, m]));
        const reordered = orderedIds.map(id => idToMsg[id]).filter(Boolean);
        // Preserve non-visible messages at the end
        const reorderedSet = new Set(orderedIds);
        const rest = messages.filter(m => !reorderedSet.has(m._id));
        const newMessages = [...reordered, ...rest];
        setMessages(newMessages);
        setFilteredMessages(newMessages);

        try {
            await apiFetch(
                `${process.env.REACT_APP_API_URL}/messages/reorder`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify({ ids: newMessages.map(m => m._id) }),
                },
                handleApiError
            );
        } catch (err) {
            console.error('Error saving message order:', err);
            fetchMessages();
        }
    };

    const handleNewMessage = () => {
        setEditingMessage(null);
        setShowEditor(true);
    };

    const handleCloseEditor = useCallback(() => {
        setEditingMessage(null);
        setShowEditor(false);
    }, []);

    // Auto-focus la modale éditeur à l'ouverture
    useEffect(() => {
        if (showEditor && editorModalRef.current) {
            // Focus le premier élément focusable à l'intérieur
            const focusable = editorModalRef.current.querySelectorAll(
                'input, textarea, select, button, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
            );
            if (focusable.length > 0) {
                focusable[0].focus();
            } else {
                editorModalRef.current.focus();
            }
        }
    }, [showEditor]);

    // Focus trap : empêche Tab de sortir de la modale éditeur
    const handleEditorModalKeyDown = useCallback((e) => {
        if (e.key !== 'Tab') return;
        const container = editorModalRef.current;
        if (!container) return;
        const focusable = Array.from(container.querySelectorAll(
            'input, textarea, select, button:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
        )).filter(el => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }, []);

    if (!activeTenantId) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-4">
                        <div className="text-6xl mb-4">📋</div>
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
                            Aucune organisation
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Pour accéder aux messages, créez une organisation ou rejoignez-en une existante.
                        </p>
                        <button
                            onClick={() => navigate('/join-tenant')}
                            className="bg-primary text-white dark:text-black font-semibold py-3 px-6 rounded hover:bg-primary-hover transition"
                        >
                            Créer ou rejoindre une organisation
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
            <Header />
            <EmailVerificationBanner />
            <main className="flex-1 flex flex-col items-center pt-4 px-4 md:px-1 container mx-auto">
                <div className="p-4 pt-0 bg-white dark:bg-gray-800 rounded-t-xl rounded-b-[1.5rem] shadow w-full">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
                        <MessageSearch
                            onSearch={handleSearch}
                            columns={columns}
                            setColumns={setColumns}
                            onImportSuccess={fetchMessages}
                            onSearchClick={() => { setShowSearchModal(true); tutorialNotify('search-opened'); }}
                            messageTypes={messageTypes}
                            messageRoles={messageRoles}
                        />
                        <div className="w-full block md:hidden" aria-hidden="true">
                            <div className="h-px bg-gray-200 dark:bg-gray-700 w-full my-2" />
                        </div>
                        <div className="flex flex-wrap items-center gap-3 justify-end">
                            {selectionMode ? (
                                <>
                                    <button
                                        onClick={handleSelectAll}
                                        className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition text-sm"
                                    >
                                        {selectedMessages.size === filteredMessages.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                                    </button>
                                    {canBulkDuplicate && (
                                        <button
                                            onClick={handleDuplicateSelected}
                                            disabled={selectedMessages.size === 0}
                                            className="flex items-center gap-1.5 bg-indigo-500 text-white font-semibold py-2 px-4 rounded hover:bg-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                        >
                                            <FiCopy className="h-4 w-4" />
                                            Dupliquer ({selectedMessages.size})
                                        </button>
                                    )}
                                    {canUseCustomTabs && (
                                        <button
                                            onClick={() => handleSort(selectedMessages)}
                                            disabled={selectedMessages.size === 0}
                                            className="flex items-center gap-1.5 bg-teal-500 text-white font-semibold py-2 px-4 rounded hover:bg-teal-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                        >
                                            <FiTag className="h-4 w-4" />
                                            Organiser ({selectedMessages.size})
                                        </button>
                                    )}
                                    <button
                                        onClick={handleDeleteSelected}
                                        disabled={selectedMessages.size === 0}
                                        className="bg-red-500 text-white font-semibold py-2 px-4 rounded hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        Supprimer ({selectedMessages.size})
                                    </button>
                                    <button
                                        onClick={handleSelectionModeToggle}
                                        className="bg-gray-500 text-white font-semibold py-2 px-4 rounded hover:bg-gray-600 transition text-sm"
                                    >
                                        Annuler
                                    </button>
                                </>
                            ) : (
                                <StatusAccessRestriction 
                                    tenant={currentTenant} 
                                    action="create_message"
                                    onRestrictedAction={showRestrictedActionToast}
                                >
                                    <div className="relative" ref={dropdownRef}>
                                        <div className="inline-flex rounded-md shadow-sm pt-0 md:pt-4" role="group">
                                            <button
                                                onClick={handleNewMessage}
                                                disabled={!tenantStatus.canCreateMessage}
                                                className="bg-primary text-white dark:text-black font-semibold py-2 px-6 rounded-l hover:bg-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-tutorial="new-message-btn"
                                            >
                                                Nouveau message
                                            </button>
                                            {isAdminOrOwner && filteredMessages.length > 0 && (
                                                <button
                                                    onClick={() => setShowDropdown(!showDropdown)}
                                                    className="bg-primary text-white dark:text-black font-semibold py-2 px-2 rounded-r border-l border-primary-hover hover:bg-primary-hover transition"
                                                    aria-label="Options supplémentaires"
                                                >
                                                    <FiChevronDown className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                            )}
                                        </div>
                                        
                                        {showDropdown && (
                                            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                                                <div className="py-1">
                                                    {canBulkDelete ? (
                                                        <button
                                                            onClick={() => {
                                                                handleSelectionModeToggle();
                                                                setShowDropdown(false);
                                                            }}
                                                            className="flex items-center w-full px-4 py-2 text-sm transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                        >
                                                            <FiEdit3 className="h-5 w-5 mr-2" />
                                                            Sélectionner plusieurs
                                                        </button>
                                                    ) : (
                                                        <div className="px-4 py-3">
                                                            <p className="text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">Plan Starter+ requis</p>
                                                            <p className="text-xs text-black dark:text-white">La selection multiple nécessite le plan Starter minimum.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </StatusAccessRestriction>
                            )}
                        </div>
                    </div>
                    <div>
                        <MessageList
                            messages={filteredMessages}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            columns={columns}
                            loading={loading}
                            openMessageId={new URLSearchParams(location.search).get('openMsg')}
                            setFullMessage={setFullMessage}
                            selectionMode={selectionMode}
                            selectedMessages={selectedMessages}
                            onMessageSelect={handleMessageSelect}
                            typeColors={typeColors}
                            onReorder={handleReorder}
                            customTabsApi={customTabsApi}
                        />
                    </div>
                </div>
                {showEditor && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                        onKeyDown={handleEditorModalKeyDown}
                        aria-modal="true"
                        role="dialog"
                    >
                        <div ref={editorModalRef} tabIndex={-1} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-xl mx-4 relative outline-none">
                            <MessageEditor
                                message={editingMessage}
                                onSave={(editingMessage && editingMessage._id) ? handleUpdateMessage : handleAddMessage}
                                onCancel={handleCloseEditor}
                            />
                        </div>
                    </div>
                )}
            </main>

            <DeleteModal
                open={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setMessagesToDelete([]);
                }}
                onConfirm={confirmDelete}
                message={messagesToDelete.length === 1 ? messagesToDelete[0] : null}
                messages={messagesToDelete.length > 1 ? messagesToDelete : null}
                isMultiple={messagesToDelete.length > 1}
            />

            {fullMessage && (
                <FullTextModal
                    message={fullMessage}
                    open={!!fullMessage}
                    onClose={() => {
                        setFullMessage(null);
                        if (location.search.includes('openMsg')) {
                            navigate('/messages', { replace: true });
                        }
                    }}
                    onCopy={handleCopy}
                    copied={copied} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSort={handleSort}
                />
            )}

            <TodoPanel
                open={todoOpen}
                onClose={() => {
                    setTodoOpen(false);
                    setTodoTargetId(null);
                }}
                highlightId={todoTargetId}
            />

            <MessageSortModal
                open={showSortModal}
                onClose={() => setShowSortModal(false)}
                selectedMessages={selectedMessages}
                customTabs={customTabs}
                onAddToTab={addMessagesToTab}
                onRemoveFromTab={removeMessagesFromTab}
                canUseCustomTabs={canUseCustomTabs}
            />

            <SearchCommandModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                messages={filteredMessages}
                onSelectMessage={handleSelectSearchMessage}
                messageTypes={messageTypes}
            />

            <KeyboardShortcutsButton />

            <Footer />
        </div>
    );
};

export default Messages;